import type { LoggerConfiguration, LogLevel } from '../logger.types'
import { BaseLogger } from './base.logger'

export class Site24x7RumLogger extends BaseLogger {
    public trace(tags: string[], message: string, args: any[]): void {
        this.logBreadcrumb(tags, `(trace) ${message}`, args)
    }

    public debug(tags: string[], message: string, args: any[]): void {
        this.logBreadcrumb(tags, `(debug) ${message}`, args)
    }

    public info(tags: string[], message: string, args: any[]): void {
        this.logBreadcrumb(tags, message, args)
    }

    public warn(tags: string[], message: string, args: any[]): void {
        this.logError(tags, `(warning) ${message}`, args)
    }

    public error(tags: string[], message: string, args: any[]): void {
        this.logError(tags, message, args)
    }

    protected getLogLevel([configuration]: LoggerConfiguration): LogLevel {
        return configuration.rumLogLevel
    }

    private logBreadcrumb(tags: string[], message: string, args: any[]): void {
        const prefix = `[${tags.join(', ')}] `

        this.addBreadCrumb(prefix + message)

        const addArgBreadCrumb = (arg: string | object, key?: string) => {
            if (typeof arg !== 'object') {
                this.addBreadCrumb(' L ' + (key ? `${key}: ` : '') + arg)
                return
            }

            // Destructure objects and log each key as it's own bread crumb.
            // Site 24x7 can not handle long lines of text, so otherwise the majority of json data is lost.
            for (const key of Object.keys(arg)) {
                let keyValue = arg[key]

                // null and undefined cannot be parsed to objects
                if (keyValue === null) {
                    keyValue = 'null'
                }

                if (keyValue === undefined) {
                    keyValue = 'undefined'
                }

                if (keyValue) {
                    addArgBreadCrumb(keyValue, key)
                }
            }
        }

        for (const arg of args) {
            addArgBreadCrumb(arg)
        }
    }

    private logError(tags: string[], message: string, args: any[]): void {
        /**
         * Report all arguments as bread crumbs to keep errors stacked by message.
         * This error reports breadcrumbs may also show up in later error breadcrumbs
         * which can be confusing if the full error message is not included in those logs.
         */
        this.logBreadcrumb(['Error', ...tags], message, args)

        this.reportError(message, tags.join(', '))
    }

    private nicelyFormatObjectToString(obj: any) {
        const str = JSON.stringify(obj, null, 2)
        return this.cleanString(str)
    }

    private cleanString(text: string) {
        return text
            .replace(/"/gi, '') // remove double quotes
            .replace(/{/gi, '') // remove open curly brace
            .replace(/}/gi, '') // remove close curly brace
            .replace(/\n/gi, '') // remove new lines
            .replace(/\r/gi, '') // remove line feeds
            .replace(/\s+/gi, ' ') // replace multiple spaces with single space
    }

    private get s247r(): (command: string, arg: any) => void {
        const s247r = (window as any)?.s247r

        if (!s247r) {
            return (c, a) => {
                console.debug('Unable to send command, s247r is not defined.')
            }
        }

        return s247r
    }

    /**
     * Add information about a step a user has taken
     * @param crumb
     */
    addBreadCrumb(crumb: any) {
        try {
            if (typeof crumb !== 'string') {
                crumb = this.nicelyFormatObjectToString(crumb)
            }

            this.s247r('addBreadCrumbs', this.cleanString(crumb))
        } catch {}
    }

    /**
     * Avoid using dynamic param in error message to allow grouping of errors within 24x7.
     * Instead, add dynamic values as bread crumbs using `addBreadCrumb`
     * @param error
     * @param type The error type, maybe helps with grouping in site24x7
     */
    reportError(error: string, type?: string) {
        try {
            const errLog = new Error(error)
            if (type) errLog.name = type

            this.s247r('captureException', errLog)
        } catch {}
    }
}
